import { createContext, useContext, useEffect, useState, useCallback } from "react";

export interface UseDashboardModuleResult<T> {
  data?: T;
  loading: boolean;
  error?: Error;
  reload: () => void;
}

export const DashboardRefreshContext = createContext<number>(0);

export function useDashboardModule<T>(
  fetcher: () => Promise<T>,
  deps: any[],
  enabled: boolean
): UseDashboardModuleResult<T> {
  const refreshNonce = useContext(DashboardRefreshContext);
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setData(undefined);
      setError(undefined);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetcher()
      .then((d) => { if (!cancelled) { setData(d); setError(undefined); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e : new Error(String(e))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled, refreshNonce]);

  return { data, loading, error, reload };
}
